import React, { useState } from 'react';
import { Link, useSearchParams, useParams, useNavigate } from 'react-router-dom';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import {
  Building2,
  // Cog,
  // Film,
  // HardDrive,
  // User2,
  Edit,
  // Search,
  X,
  // ExternalLink,
  Folder,
  Plus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import SiteLocationMap from '@/components/maps/SiteLocationMap';
// import { DatalayerAssetDto, ComponentType } from '@/types/datalayer-asset';
import EditCustomerModal from './components/EditCustomerModal';
// import { useAuthStore } from '@/store/authStore';
import { FetchCustomerQuery } from '@/lib/api/customer/customer';
import { axiosClientAdmin } from '@/lib/auth';
import { SitesSearchDto } from '@/app/Sites/siteApi';
import { AddressDto } from '@/types/common-types';

// Customer interface to match the data structure from Viewer2
// interface HierarchyDevice extends DatalayerAssetDto {
//   siteName: string;
//   siteId: string;
// }

// interface HierarchySite extends SitesSearchDto {
//   devices: HierarchyDevice[];
// }

interface Customer {
  id: string;
  name: string;
  hasContracts: boolean;
  accountNumber: string;
  archived: boolean;
  email?: string;
  phoneNumber?: string;
  address?: AddressDto;
  sites: SitesSearchDto[];
}

// Type for HierarchyCustomer from Viewer2 compatibility
type HierarchyCustomer = {
  id: string;
  name: string;
  hasContracts: boolean;
  accountNumber: string;
  archived: boolean;
  email?: string;
  phoneNumber?: string;
  address?: AddressDto;
  sites: Array<SitesSearchDto>;
};

interface CustomerComponentProps {
  customerId?: string; // Accept customerId for loading customer data
  customer?: Customer | HierarchyCustomer; // Accept customer object directly
  onCustomerUpdate?: (updatedCustomer: Partial<Customer> | Partial<HierarchyCustomer>) => void;
  // Add prop to disable URL parameter management when used in viewer
  disableUrlParams?: boolean;
}

export default function CustomerComponent({
  customerId: propCustomerId,
  customer: propCustomer,
  onCustomerUpdate,
  disableUrlParams = false,
}: CustomerComponentProps) {
  const params = useParams();
  const customerId = propCustomerId || params.customerId || propCustomer?.id;
  const navigate = useNavigate();
  
  console.log('CustomerComponent render:', { customerId, propCustomerId, params });
  const [mapLat] = useState<number | undefined>(-36.8509);
  const [mapLon] = useState<number | undefined>(174.7645);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  // const authStore = useAuthStore();

  // Search and view state
  const [sitesSearchTerm, setSitesSearchTerm] = useState('');
  const [sitesViewMode] = useState<'grid' | 'table'>('table');
  // const [devicesSearchTerm, setDevicesSearchTerm] = useState('');
  // const [devicesViewMode] = useState<'grid' | 'table'>('table');

  // Local tab state for when URL params are disabled (e.g., in viewer)
  const [localTab, setLocalTab] = useState('sites');

  // Get the current tab from URL params or local state, default to 'sites'
  const currentTab = disableUrlParams ? localTab : searchParams.get('tab') || 'sites';

  // Function to handle tab changes - use URL or local state based on context
  const handleTabChange = (value: string) => {
    if (disableUrlParams) {
      setLocalTab(value);
    } else {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set('tab', value);
      setSearchParams(newSearchParams);
    }
  };

  // Fetch customer data using useQuery like AppBreadcrumbs (only if propCustomer not provided)
  const { data: fetchedCustomer, isLoading: customerLoading } = useQuery({
    ...FetchCustomerQuery({ customerId: customerId ?? '' }),
    enabled: Boolean(customerId) && !propCustomer,
    staleTime: 300000, // Cache for 5 minutes
  });

  // Use propCustomer if provided, otherwise transform fetched customer to Customer format
  const customer: Customer | HierarchyCustomer | undefined = propCustomer || (fetchedCustomer ? {
    id: fetchedCustomer.id || '',
    name: fetchedCustomer.name || '',
    hasContracts: false, // CustomerDto doesn't have hasContracts, defaulting to false
    accountNumber: fetchedCustomer.accountNumber || '',
    archived: fetchedCustomer.archived || false,
    email: fetchedCustomer.email || undefined,
    phoneNumber: fetchedCustomer.phoneNumber || undefined,
    address: fetchedCustomer.address,
    sites: [] // Sites will be populated from sitesData
  } : undefined);

  // Fetch sites using infinite query only if customer object doesn't have sites
  const shouldFetchSites = Boolean(customerId) && !propCustomer?.sites?.length;
  
  const {
    data: sitesData,
    isLoading: sitesLoading,
  } = useInfiniteQuery({
    queryKey: ['customer-sites-paginated', customerId, sitesSearchTerm],
    initialPageParam: 1,
    queryFn: async ({ pageParam = 1 }) => {
      const actualPageSize = pageParam === 1 ? Math.min(10, 20) : 20;
      
      const response = await axiosClientAdmin.get<{rows: SitesSearchDto[], totalRows: number}>(
        `/Customer/${customerId}/project/search`,
        {
          params: {
            PageNumber: pageParam,
            PageSize: actualPageSize,
            SortBy: 'name',
            SortDir: 'Ascending',
          },
        }
      );

      const sites = response.data.rows || [];
      
      // Apply client-side search filtering if search term exists
      const filteredSites = sitesSearchTerm 
        ? sites.filter(site => 
            site.name?.toLowerCase().includes(sitesSearchTerm.toLowerCase()) ||
            site.address1?.toLowerCase().includes(sitesSearchTerm.toLowerCase())
          )
        : sites;
      
      return {
        sites: filteredSites,
        nextPage: (response.data.totalRows || 0) > pageParam * actualPageSize ? pageParam + 1 : undefined,
        totalRows: response.data.totalRows || 0,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    staleTime: 300000,
    enabled: shouldFetchSites,
  });

  // Use sites from customer object if available (from Viewer2), otherwise use fetched sites
  const customerSites = customer?.sites || [];
  const fetchedSites = sitesData?.pages.flatMap(page => page.sites) || [];
  const allSites = customerSites.length > 0 ? customerSites : fetchedSites;
  
  // Apply search filtering to customer sites if needed
  const filteredSites = sitesSearchTerm && customerSites.length > 0
    ? customerSites.filter(site => 
        site.name?.toLowerCase().includes(sitesSearchTerm.toLowerCase()) ||
        site.address1?.toLowerCase().includes(sitesSearchTerm.toLowerCase())
      )
    : allSites;

  // Calculate statistics from sites data
  const sitesCount = filteredSites.length;
  // const devicesCount = allDevices.length;
  
  // const cameraFeedsCount = allDevices.reduce((total, device) => {
  //   const videoConfigCount = device.videoProcessorConfigs?.length || 0;
  //   const cameraComponentCount =
  //     device.components?.filter((component) => component.type === ComponentType.Camera)
  //       .length || 0;
  //   return total + Math.max(videoConfigCount, cameraComponentCount);
  // }, 0);

  // Sites are already filtered above
  //const filteredDevices = allDevices;

  // Show loading state if any data is loading
  if (customerLoading || sitesLoading) {
    return (
      <section className="m-4">
        <div className="mx-auto mt-8 max-w-[1280px] text-center">
          <div className="text-gray-400">Loading...</div>
        </div>
      </section>
    );
  }




  // If no customer found, show error
  if (!customer && !customerLoading) {
    return (
      <section className="m-4">
        <div className="mx-auto mt-8 max-w-[1280px] text-center">
          <div className="text-gray-400">Customer not found.</div>
        </div>
      </section>
    );
  }

  // If customer has no data, show empty state page

  return (
    <section className="m-4">
      <section data-onboarding="customer-detail-header" className="mx-auto mt-8 flex max-w-[1280px] items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Folder className="h-6 w-6" />
            <div className="flex items-center gap-2">
              <h2 className="m-0 text-2xl font-semibold">{customer?.name || 'Customer Name'}</h2>
            </div>
          </div>
          <div className="flex items-center gap-2"></div>
        </div>{' '}
        {/* <div className="flex items-center gap-12">
          <div className="">
            <div className="flex items-center gap-1.5">
              <Building2 className="h-4 w-4" />
              <h4 className="m-0 font-semibold">Sites</h4>
            </div>
            <p className="m-0 text-3xl font-semibold">{sitesCount}</p>
          </div>
          <div className="">
            <div className="flex items-center gap-1.5">
              <HardDrive className="h-4 w-4" />
              <h4 className="m-0 font-semibold">Devices</h4>
            </div>
            <p className="m-0 text-3xl font-semibold">{devicesCount}</p>
          </div>
          <div className="">
            <div className="flex items-center gap-1.5">
              <Film className="h-4 w-4" />
              <h4 className="m-0 font-semibold">Feeds</h4>
            </div>
            <p className="m-0 text-3xl font-semibold">{cameraFeedsCount}</p>
          </div>
        </div> */}
      </section>
      <section className="mx-auto max-w-[1280px]">
        {/* Customer Information - Read Only */}
        <div className="mt-4 rounded-xl border bg-card1 p-4" data-onboarding="customer-detail-contact-info">
          <div className="space-y-4">
            {/* Contact Information */}

            {/* Address & Map */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-4">
                <div className="border-b pb-4">
                  <label className="text-xs font-medium text-gray-400">Account Number</label>
                  <div className="text-sm text-white">
                    {customer?.accountNumber || 'Not provided'}
                  </div>
                </div>
                <div className="border-b pb-4">
                  <label className="text-xs font-medium text-gray-400">Email</label>
                  <div className="text-sm text-white">{customer?.email || 'Not provided'}</div>
                </div>
                <div className="border-b pb-4">
                  <label className="text-xs font-medium text-gray-400">Phone Number</label>
                  <div className="text-sm text-white">
                    {customer?.phoneNumber || 'Not provided'}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-400">Address</label>
                  {customer?.address?.addressLine1 ? (
                    <div className="text-sm text-white">
                      <div>{customer.address.addressLine1}</div>
                      {customer.address.addressLine2 && <div>{customer.address.addressLine2}</div>}
                      <div>
                        {[customer.address.city, customer.address.region, customer.address.postCode]
                          .filter(Boolean)
                          .join(', ')}
                      </div>
                      {customer.address.country && <div>{customer.address.country}</div>}
                    </div>
                  ) : (
                    <div className="text-sm text-gray-400">No address provided</div>
                  )}
                </div>
                <div className="pt-4">
                  <Button
                    onClick={() => setEditModalOpen(true)}
                    className="w-full"
                    variant="outline"
                    data-onboarding="customer-detail-edit-button"
                  >
                    <Edit className="mr-2 h-4 w-4" />
                    Edit Customer
                  </Button>
                </div>
              </div>
              <div>
                <div className="h-[360px] overflow-hidden rounded-lg">
                  <SiteLocationMap
                    coordinates={{
                      lat: customer?.address?.lat || mapLat || -36.8509,
                      lng: customer?.address?.lon || mapLon || 174.7645,
                    }}
                    initialAddress={customer?.address?.addressLine1 || ''}
                    onCoordinatesChange={() => {}}
                    onAddressChange={() => {}}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <div >
      <Tabs
        value={currentTab}
        onValueChange={handleTabChange}
        className="mx-auto mt-4 max-w-[1280px]" data-onboarding="customer-detail-content"
      >
        {/* <TabsList className="w-full rounded-none border-0 border-b-2">
          <TabsTrigger value="sites" className="">
            Sites <div className="ml-2 rounded-lg border px-2 py-0.5 text-white">{sitesCount}</div>
          </TabsTrigger> */}
          {/* <TabsTrigger value="devices">
            Devices{' '}
            <div className="ml-2 rounded-lg border px-2 py-0.5 text-white">{devicesCount}</div>
          </TabsTrigger> */}
        {/* </TabsList> */}
        <TabsContent value="sites">
          <div className="mt-4">
            <div className="mb-4 flex items-center justify-between rounded-xl border bg-card1 p-4">
              <div className="flex items-center gap-2">
                {/* Search Input */}
                <div className="relative">
                  <Input
                    isSearch
                    placeholder="Search sites..."
                    value={sitesSearchTerm}
                    onChange={(e) => setSitesSearchTerm(e.target.value)}
                    className="w-64"
                  />
                  {sitesSearchTerm && (
                    <button
                      onClick={() => setSitesSearchTerm('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 transform rounded p-1 hover:bg-gray-700"
                    >
                      <X className="h-3 w-3 text-gray-400 hover:text-white" />
                    </button>
                  )}
                </div>

              </div>
              <div>
                {/* <a
                  href={`/site/new/${customerId}`}
                  className="rounded-md border px-4 py-2.5 text-sm font-semibold text-lime"
                >
                  Add a new site test
                </a> */}
                <Button
                          size="sm"
                          onClick={() => navigate(`/site/new/${customerId}`)}
                          data-onboarding="sites-add-button"
                        >
                          <Plus className="mr-2 h-4 w-4" />
                          Add Site
                        </Button>
              </div>
            </div>

            {filteredSites.length > 0 ? (
              sitesViewMode === 'grid' ? (
                <div className="grid grid-cols-3 gap-4">
                  {filteredSites.map((site) => (
                    <Link
                      key={site.id}
                      to={`/Customer/${customerId}/project/${site.id}`}
                      className="flex flex-col justify-between rounded-lg border bg-card1 p-4 transition-colors hover:bg-card2"
                    >
                      <div className="flex items-center justify-between">
                        <div className=" flex w-full items-center gap-2">
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-lime" />
                            <h3 className="font-semibold">{site.name}</h3>
                          </div>
                        </div>
                      </div>{' '}
                      {/* <div className="flex items-center gap-1 text-sm text-gray-400">
                        <HardDrive className="h-4 w-4" />
                        {site.devices?.length || 0} devices
                      </div> */}
                      {site.address1 && (
                        <div className="mt-2 text-sm text-gray-400">{site.address1}</div>
                      )}
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="overflow-hidden rounded-xl border">
                  <Table className="">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Location</TableHead>
                        <TableHead>Status</TableHead>
                        {/* <TableHead>Devices</TableHead> */}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredSites.map((site) => (
                        <TableRow key={site.id}>
                          <TableCell>
                            <Link
                              to={`/Customer/${customerId}/project/${site.id}`}
                              className="text-sm text-lime hover:underline"
                            >
                              <div className="flex items-center gap-2">
                                <Building2 className="h-4 w-4" />
                                <span className="font-semibold">{site.name}</span>
                              </div>
                            </Link>
                          </TableCell>
                          <TableCell>
                            {site.address1 ? (
                              <span className="text-sm text-gray-400">{site.address1}</span>
                            ) : (
                              <span className="text-sm text-gray-500">No location</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant={site.isActive ? 'default' : 'secondary'}>
                              {site.isActive ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                          {/* <TableCell>
                            <span className="text-sm text-gray-400">
                              {site.devices?.length || 0} devices
                            </span>
                          </TableCell> */}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )
            ) : (
              <div className="mt-8 text-center text-gray-400">
                <Building2 className="mx-auto h-12 w-12 opacity-20" />
                <p className="mt-2">
                  {sitesSearchTerm ? 'No sites match your search' : 'No sites found'}
                </p>
                <a
                  href="/site/new"
                  className="rounded border border-lime px-2 py-1 text-sm text-lime hover:underline"
                >
                  Add a new site
                </a>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
      </div>

      <EditCustomerModal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        customer={customer}
        onCustomerUpdate={onCustomerUpdate}
      />
    </section>
  );
}
